// activity.service.ts
import { Injectable, Inject, NgZone } from '@angular/core';
import { Router, NavigationEnd, ActivationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { LogModel, TipoEvento } from '../models/log-model.model';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from './authentication.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ActivityService {
  private _inited = false;
  private _domWired = false;
  private _lastRouteKey?: string;
  private currentComponentName?: string;   // nombre del componente activo
  private sesionId = this.safeRandomUUID();
  private trackEnabled = false;
  private readonly sessionSample =
    Math.random() < Math.max(0, Math.min(1, environment.trackSampleRate ?? 1));
  // Solo elementos de acción (NO inputs/selects/textarea)
  private static readonly CLICKABLE_SELECTOR = [
    'button:not(:disabled)',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
    '[role="button"]:not([aria-disabled="true"])',
    '[role="link"]:not([aria-disabled="true"])',
    '[role="menuitem"]:not([aria-disabled="true"])',
    '[role="tab"]:not([aria-disabled="true"])',
    // librerías (ajusta si usas otras)
    '.mat-mdc-button,.mat-mdc-icon-button,.mat-mdc-menu-item,.mat-mdc-tab,.mat-sort-header',
    // routing
    '[routerLink]',
    // tus elementos custom que disparan acciones
    '[data-clickable]'
  ].join(',');
  private clickableEls = new WeakSet<HTMLElement>();

  // ¿Quieres loguear el valor literal? (true) o enmascararlo por defecto (false)
  private readonly CAPTURAR_VALOR_PLANO = true;

  // Patrones de nombres/ids sensibles a enmascarar SI CAPTURAR_VALOR_PLANO=false
  private static readonly SENSITIVE_NAME_RE = /(pass|password|contrase|iban|cuenta|card|tarjeta|nif|dni|ssn)/i;

  private lastValueMap = new WeakMap<HTMLElement, any>(); // guarda el último valor "raw" por elemento


  // Campos de formulario (para Input/Focus/Blur y “cambios”)
  private static readonly FORM_FIELD_SELECTOR = [
    'input:not([type="hidden"]):not(:disabled)',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    '[contenteditable="true"]',
    '[role="combobox"]'
  ].join(',');

  // Sesiones de edición activas por elemento
  private editSessions = new WeakMap<HTMLElement, {
    startTs: number;
    initial: any;
    last: any;
    tipo: string;
    campo?: string;
    changes: number;
    // nuevo:
    changed: boolean;          // hubo cambio durante la edición
    timer?: any;               // fallback si no hay blur
  }>();

  private editingSet = new Set<HTMLElement>(); // para poder vaciar al salir de la página
    
  // Throttle para input
  private readonly INPUT_THROTTLE_MS = 300;
  private lastInputTs = new WeakMap<HTMLElement, number>();

  // Excluir “fondos”/backdrops/containers
  private static readonly EXCLUDE_CLICK_SELECTOR = [
    'html','body','.cdk-overlay-backdrop','.cdk-overlay-container'
  ].join(',');

  private rutaActual = '';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private ngZone: NgZone,
    private authenticationService: AuthenticationService,
    private http: HttpClient
  ) {}

  init() {
    if (this._inited) return;   // 👈 no repetir
    this._inited = true;
    const baseGate =
        Boolean(environment.trackUserActivity) &&
        this.sessionSample 
    const isAdmin = this.authenticationService.getTipoUser() === "1";
    this.trackEnabled = baseGate && !isAdmin;
    this.installListenerSniffer();
    // Rutas (1 solo log por NavigationEnd)
    this.router.events.subscribe(ev => {
      // Captura el componente del outlet primario
      if (ev instanceof ActivationEnd && ev.snapshot.outlet === 'primary') {
        this.currentComponentName = this.findComponentName(ev.snapshot);
      }

      // Solo log en NavigationEnd + dedupe
      if (ev instanceof NavigationEnd) {
        this.rutaActual = ev.urlAfterRedirects || ev.url;
        const routeKey = `${this.rutaActual}|${this.currentComponentName ?? ''}`;
        if (routeKey !== this._lastRouteKey) {
          this._lastRouteKey = routeKey;
          this.emit('Route', undefined, 'Cambio de ruta');
        }
      }
    });
    // Delegación de eventos global
    this.ngZone.runOutsideAngular(() => {
      const doc = this.document;
      doc.addEventListener('click',     e => this.handleDom(e), { passive: true });

      // focusin
      doc.addEventListener('focusin', e => {
        const t = (e.composedPath?.[0] || e.target) as HTMLElement | null;
        const field = this.findFormFieldTarget(t);
        if (field) this.beginEdit(field);
      }, { passive: true });

      // input (solo para campos reales; en ng-select el input interno NO disparará log)
      doc.addEventListener('input', e => {
        const t = (e.composedPath?.[0] || e.target) as HTMLElement | null;
        const field = this.findFormFieldTarget(t);
        if (!field) return;
        const now = Date.now();
        const last = this.lastInputTs.get(field) || 0;
        if (now - last < this.INPUT_THROTTLE_MS) return;
        this.lastInputTs.set(field, now);
        this.updateEdit(field);
      }, { passive: true });

      // change → programa commit (por si no hay blur)
      doc.addEventListener('change', e => {
        const t = (e.composedPath?.[0] || e.target) as HTMLElement | null;
        const field = this.findFormFieldTarget(t);
        if (!field) return;
        this.updateEdit(field);
        this.queueCommit(field, 'change');
      }, { passive: true });

      // blur → commit definitivo
      doc.addEventListener('focusout', e => {
        const t = (e.composedPath?.[0] || e.target) as HTMLElement | null;
        const field = this.findFormFieldTarget(t);
        if (field) this.finalizeEdit(field, 'blur');
      }, { passive: true });

      doc.addEventListener('keydown',   e => this.handleDom(e), { passive: true });
      
      // Si cierra/navega sin blur → emite pendientes
      window.addEventListener('beforeunload', () => {
        // recorre sesiones activas y finaliza
        const entries: HTMLElement[] = [];
        // WeakMap no es iterable; guarda también un Set si quieres trackear abiertos
        // o llama finalizeEdit sobre document.activeElement si es un field:
        const active = this.document.activeElement as HTMLElement | null;
        if (active && this.findFormFieldTarget(active)) {
          this.finalizeEdit(active, 'leave');
        }
      });
    });
  }

  private isFormFieldElement(el: Element): boolean {
    return el.matches(ActivityService.FORM_FIELD_SELECTOR)
        || el.hasAttribute('formControlName')
        || el.hasAttribute('ng-reflect-form-control-name')
        || el instanceof HTMLInputElement
        || el instanceof HTMLSelectElement
        || el instanceof HTMLTextAreaElement;
  }
  
  // === Parchar addEventListener para detectar "clickables" automáticamente =====
  private installListenerSniffer() {
    const proto = EventTarget.prototype as any;
    if (proto.__ua_patched_click_sniffer) return;
    proto.__ua_patched_click_sniffer = true;

    const origAdd = proto.addEventListener as EventTarget['addEventListener'];
    const self = this;

    proto.addEventListener = function(type: string, listener: any, options?: any) {
      if ((type === 'click' || type === 'pointerup' || type === 'mousedown') && this instanceof HTMLElement) {
        self.clickableEls.add(this as HTMLElement);
      }
      return (origAdd as any).call(this, type, listener, options);
    };
  }

  private isLabelForFormField(el: HTMLElement): boolean {
    if (el.tagName?.toLowerCase() !== 'label') return false;
    const forId = el.getAttribute('for');
    if (!forId) return false;
    const target = el.ownerDocument?.getElementById(forId);
    return !!target && this.isFormFieldElement(target);
  }


  private safeRandomUUID(): string {
    const g: any = (typeof globalThis !== 'undefined' ? globalThis : window) as any;
    const c: any = g.crypto || g.msCrypto;

    // 1) Soporte nativo (si existe)
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID();
    }

    // 2) UUID v4 con getRandomValues (estándar)
    if (c && typeof c.getRandomValues === 'function') {
      const buf = new Uint8Array(16);
      c.getRandomValues(buf);
      // version y variant
      buf[6] = (buf[6] & 0x0f) | 0x40;
      buf[8] = (buf[8] & 0x3f) | 0x80;
      const hex = Array.from(buf, b => b.toString(16).padStart(2, '0'));
      return `${hex.slice(0,4).join('')}-${hex.slice(4,6).join('')}-${hex.slice(6,8).join('')}-${hex.slice(8,10).join('')}-${hex.slice(10,16).join('')}`;
    }

    // 3) Fallback simple (menos seguro pero suficiente para sesión)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
      const r = (Math.random() * 16) | 0;
      const v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private findInteractiveTarget(start: HTMLElement | null): HTMLElement | null {
    if (!start) return null;
    const bad = (el: Element) => el.matches(ActivityService.EXCLUDE_CLICK_SELECTOR);

    let el: HTMLElement | null = start.nodeType === 1 ? start : start?.parentElement || null;
    while (el && el !== el.ownerDocument?.documentElement) {
      if (bad(el)) return null;
      if (el.hasAttribute('data-no-track')) return null; // opt-out

      // ⛔️ Sal si es un campo de formulario o un label que apunta a un campo
      if (this.isFormFieldElement(el) || this.isLabelForFormField(el)) return null;

      // ✅ Click “válido” (listener detectado o coincide con whitelist de acción)
      if (this.clickableEls.has(el)) return el;
      if (el.matches(ActivityService.CLICKABLE_SELECTOR)
          && !el.matches(':disabled,[aria-disabled="true"]')) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  private tryHandleSelectLikeClick(e: MouseEvent): boolean {
    const t = (e.composedPath?.[0] || e.target) as HTMLElement | null;
    if (!t) return false;

    // Opción pulsada en ng-select / mat-select / PrimeNG
    const option = t.closest('.ng-option, mat-option, [role="option"], .p-dropdown-item, .p-multiselect-item') as HTMLElement | null;
    if (!option) return false;

    const combo = this.findComboboxForOption(option);
    if (!combo) return false;

    const host = this.normalizeComboElement(combo);

    // Abre sesión si no existe
    this.beginEdit(host);

    // Tras aplicar la selección, actualiza y programa commit (1 solo log)
    setTimeout(() => {
      this.updateEdit(host);            // lee texto visible nuevo
      this.queueCommit(host, 'change'); // emitirá al blur o por timeout
    }, 0);

    // Evita registrar además un Click sobre la opción
    return true;
  }

  // === ÚNICO manejador para todos los DOM events ==============================
  private handleDom(e: Event) {
    const type = e.type; // 'click' | 'input' | 'change' | 'focusin' | 'focusout' | 'keydown'
    const target = (e.composedPath?.()[0] || e.target) as HTMLElement | null;
    if (!target) return;

    // CLICK: sólo si es elemento interactivo (whitelist o con listener)
    if (type === 'click') {
      const me = e as MouseEvent;
      if (me.button !== 0 || (me as any).isTrusted === false) return;


      // 👇 Si el click es en una opción de ng-select/mat-select, lo tratamos como cambio de campo
      if (this.tryHandleSelectLikeClick(me)) return;
      
      const interactive = this.findInteractiveTarget(target);
      if (!interactive) return; // 👉 clicks en fondo o en campos → NO se loguean

      const meta = this.describeElement(interactive);
      this.emit('Click', meta, this.describeAction('Click', meta), { X: me.clientX, Y: me.clientY });
      return;
    }

    if (type === 'input' || type === 'change') {
      const field = this.findFormFieldTarget(target);
      if (!field) return;

      const snap = this.getFieldSnapshot(field);
      const prev = this.lastValueMap.get(field);
      const curr = snap.valor;

      // Throttle (solo para 'input')
      const now = Date.now();
      const last = this.lastInputTs.get(field) || 0;
      if (type === 'input' && now - last < this.INPUT_THROTTLE_MS) return;
      this.lastInputTs.set(field, now);

      // Si no cambió realmente, evita ruido (especialmente en 'change')
      if (this.valuesEqual(prev, curr) && type === 'change') return;

      // Guarda nuevo valor "raw" para próximas comparaciones
      this.lastValueMap.set(field, curr);

      const maskedNew = this.maskValue(field, curr);
      const maskedOld = this.maskValue(field, prev);

      const meta = this.describeElement(field);
      const accion = this.describeAction('Input', meta);

      this.emit('Input', meta, accion, {
        TipoControl: snap.tipo,
        Campo: this.fieldKey(field),
        ValorAnterior: maskedOld.val,
        ValorNuevo: maskedNew.val,
        OpcionTexto: snap.opcionTexto,
        Checked: snap.checked,
        Enmascarado: maskedNew.masked || maskedOld.masked,
        MascaraEstrategia: maskedNew.strategy
      });
      return;
    }

    if (type === 'focusin' || type === 'focusout') {
      const field = this.findFormFieldTarget(target) || this.findInteractiveTarget(target);
      if (!field) return;

      // En focus/blur no forzamos cambio, pero puedes loguear snapshot rápido:
      if (type === 'focusout') {
        const snap = this.getFieldSnapshot(field);
        const prev = this.lastValueMap.get(field);
        const curr = snap.valor;
        if (!this.valuesEqual(prev, curr)) {
          this.lastValueMap.set(field, curr);
          const maskedNew = this.maskValue(field, curr);
          const maskedOld = this.maskValue(field, prev);
          const meta = this.describeElement(field);
          this.emit('Input', meta, this.describeAction('Input', meta), {
            TipoControl: snap.tipo,
            Campo: this.fieldKey(field),
            ValorAnterior: maskedOld.val,
            ValorNuevo: maskedNew.val,
            OpcionTexto: snap.opcionTexto,
            Checked: snap.checked,
            Enmascarado: maskedNew.masked || maskedOld.masked,
            MascaraEstrategia: maskedNew.strategy
          });
        }
      }

      const meta = this.describeElement(field);
      this.emit(type === 'focusin' ? 'Focus' : 'Blur', meta, this.describeAction(type === 'focusin' ? 'Focus' : 'Blur', meta));
      return;
    }

  // KEYDOWN: ignora caracteres (privacidad); registra teclas "de control"
  if (type === 'keydown') {
    const ke = e as KeyboardEvent;
    if (!this.shouldLogKey(ke)) return;

    // Sólo si el foco está en algo relevante
    const active = (this.document.activeElement as HTMLElement | null) || target;
    const field = this.findFormFieldTarget(active) || this.findInteractiveTarget(active);
    if (!field) return;

    const meta = this.describeElement(field);
    this.emit('KeyDown', meta, `Key: ${ke.key}`, { Tecla: ke.key });
    return;
  }
}

  private findFormFieldTarget(start: HTMLElement | null): HTMLElement | null {
    if (!start) return null;
    const isField = (el: Element) =>
      el.matches('input:not([type="hidden"]):not(:disabled),select:not(:disabled),textarea:not(:disabled),[contenteditable="true"],[role="combobox"]') ||
      el.hasAttribute('formcontrolname') || el.hasAttribute('ng-reflect-form-control-name');

    let el: HTMLElement | null = start.nodeType === 1 ? start : start?.parentElement || null;
    while (el && el !== el.ownerDocument?.documentElement) {
      if (el.hasAttribute('data-no-track')) return null;
      if (isField(el)) {
        return this.normalizeComboElement(el); // 👈 clave
      }
      el = el.parentElement;
    }
    return null;
  }


private shouldLogKey(e: KeyboardEvent): boolean {
  // No letras ni números (privacidad). Sí Escape/Enter/Tab/Arrows/etc.
  if (!e.isTrusted) return false;
  if (e.key.length === 1) return false;
  // Puedes filtrar más si quieres:
  // const allowed = ['Escape','Enter','Tab','ArrowUp','ArrowDown','ArrowLeft','ArrowRight','F1','F2','F3','Backspace','Delete'];
  // return allowed.includes(e.key);
  return true;
}

  private handleKey(e: KeyboardEvent) {
    // No registrar letras/números para privacidad
    if (e.key?.length === 1) return;
    const t = (e.composedPath?.()[0] || e.target) as HTMLElement | null;
    const meta = this.describeElement(t || undefined);
    this.emit('KeyDown', meta, `Key: ${e.key}`, { Tecla: e.key });
  }

  public registrarDiagnostico(accion: string, datosExtra: Record<string, unknown>, esError: boolean = false): void {
    this.emit(esError ? 'Error' : 'Input', undefined, accion, {
      EsInteraccionUsuario: false,
      EsError: esError,
      // Compatibilidad con la API actualmente publicada: el detalle visible en
      // el visor de actividad se persiste en ValorNuevo.
      ValorNuevo: datosExtra,
      DatosExtra: datosExtra
    });
  }

  private emit(tipo: TipoEvento, el?: ReturnType<ActivityService['describeElement']>, accion?: string, extra?: Partial<LogModel>) {
    if (!this.trackEnabled) return; // ⛔️ tracking OFF por env/rol/sampling/DNT
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const log: LogModel = {
      SesionId: this.sesionId,
      CorrelationId: undefined,
      UsuarioId: undefined,

      Pantalla: this.rutaActual || location.pathname,
      Component: this.currentComponentName,
      Url: location.href,
      Referrer: document.referrer,
      FechaHoraUtc: new Date().toISOString(),
      ZonaHoraria: tz,

      TipoEvento: tipo,
      Accion: accion,
      Click: el?.Selector,                // compat si quieres
      Tecla: extra?.Tecla,
      EsInteraccionUsuario: true,

      Tag: el?.Tag,
      Id: el?.Id,
      Clases: el?.Clases,
      Name: el?.Name,
      FormControlName: el?.FormControlName,
      Placeholder: el?.Placeholder,
      Label: el?.Label,
      Selector: el?.Selector,

      X: extra?.X,
      Y: extra?.Y,

      UserAgent: navigator.userAgent,
      ViewportAncho: window.innerWidth,
      ViewportAlto: window.innerHeight,

      // Ip / Localizacion las pondrá el backend
      DatosExtra: undefined,
      ...extra
    };

    let headersToken: HttpHeaders = new HttpHeaders({
        "Authorization": "Bearer " + this.authenticationService.getToken()
      });
  
      const url_api = environment.apiUrl + "txgl_activityUser";
      
      return this.http.post(url_api, JSON.stringify(log), {headers: headersToken.append("Content-Type", "application/json") }).pipe(map(data => data)).subscribe(d => {
      });
  }

  private findComponentName(snap: ActivatedRouteSnapshot): string | undefined {
    let s: ActivatedRouteSnapshot | null = snap;
    while (s?.firstChild) s = s.firstChild;             // hoja activa
    const ctor: any = s?.component;
    const byCtor = ctor?.name;                          // AppComponent
    const byAot  = ctor?.ɵcmp?.type?.name;              // fallback AOT
    const byData = s?.data?.['title'];                  // si usas title en ruta
    const byPath = s?.routeConfig?.path;
    return byCtor || byAot || byData || byPath || undefined;
  }

  private describeAction(tipo: TipoEvento, el?: ReturnType<ActivityService['describeElement']>) {
    if (!el) return tipo;
    const id = el.Id ? `#${el.Id}` : '';
    const name = el.Name ? `[name="${el.Name}"]` : '';
    const fcn = el.FormControlName ? `[formControlName="${el.FormControlName}"]` : '';
    const label = el.Label ? ` (${el.Label})` : '';
    return `${tipo} ${el.Tag}${id}${name}${fcn}${label}`.trim();
  }

  private describeElement(el?: HTMLElement) {
    if (!el) return undefined;
    const tag = el.tagName?.toLowerCase();
    const id = el.id || undefined;
    const classes = el.classList?.length ? Array.from(el.classList) : undefined;
    const name = el.getAttribute('name') || undefined;
    const fcn = el.getAttribute('formControlName') || undefined;
    const placeholder = el.getAttribute('placeholder') || undefined;
    const label = this.findLabelText(el);
    const selector = this.cssPath(el);
    return { Tag: tag, Id: id, Clases: classes, Name: name, FormControlName: fcn, Placeholder: placeholder, Label: label, Selector: selector };
  }

  private findLabelText(el: HTMLElement) {
    const id = el.getAttribute('id');
    if (id) {
      const lbl = this.document.querySelector(`label[for="${id}"]`);
      if (lbl) return (lbl.textContent || '').trim().slice(0, 80);
    }
    const aria = el.getAttribute('aria-labelledby');
    if (aria) {
      const txt = aria.split(/\s+/)
        .map(i => this.document.getElementById(i)?.textContent?.trim() || '')
        .filter(Boolean).join(' ');
      if (txt) return txt.slice(0, 80);
    }
    const maybe = el.closest('label') || el.parentElement?.querySelector('label');
    return (maybe?.textContent || '').trim().slice(0, 80) || undefined;
  }

  private cssPath(el: Element) {
    const parts: string[] = [];
    let node: Element | null = el;
    while (node && node.nodeType === 1 && parts.length < 6) {
      const name = node.nodeName.toLowerCase();
      if ((node as HTMLElement).id) {
        parts.unshift(`#${(node as HTMLElement).id}`);
        break;
      } else {
        const cls = (node as HTMLElement).className?.toString().trim().split(/\s+/).filter(Boolean).slice(0,2);
        const idx = Array.from(node.parentElement?.children || []).indexOf(node);
        parts.unshift(`${name}${cls?.length ? '.'+cls.join('.') : ''}:nth-child(${idx+1})`);
      }
      node = node.parentElement;
    }
    return parts.join(' > ');
  }

  private getFieldSnapshot(el: HTMLElement): {
  tipo: string; valor: any; opcionTexto?: string | string[]; checked?: boolean;
  } {
    const host = this.normalizeComboElement(el);

    // ng-select
    if (host.classList.contains('ng-select')) {
      const text = this.readComboboxText(host);
      return { tipo: 'select', valor: text, opcionTexto: text };
    }

    // mat-select
    if (host.classList.contains('mat-select')) {
      const text = this.readComboboxText(host);
      return { tipo: 'select', valor: text, opcionTexto: text };
    }

    // nativos
    if (host instanceof HTMLInputElement) {
      const t = (host.type || 'text').toLowerCase();
      if (t === 'checkbox') return { tipo: 'checkbox', valor: host.checked, checked: host.checked };
      if (t === 'radio')    return { tipo: 'radio',    valor: host.checked ? host.value : null, checked: host.checked };
      if (t === 'file')     return { tipo: 'file',     valor: host.files ? host.files.length : 0 };
      return { tipo: 'input', valor: host.value };
    }
    if (host instanceof HTMLTextAreaElement) return { tipo: 'textarea', valor: host.value };
    if (host instanceof HTMLSelectElement) {
      if (host.multiple) {
        const vals: string[] = [];
        const textos: string[] = [];
        Array.from(host.selectedOptions).forEach(o => { vals.push(o.value); textos.push((o.textContent || '').trim()); });
        return { tipo: 'select', valor: vals, opcionTexto: textos };
      } else {
        const opt = host.selectedOptions?.[0];
        return { tipo: 'select', valor: host.value, opcionTexto: opt ? (opt.textContent || '').trim() : undefined };
      }
    }
    if (host.hasAttribute('contenteditable')) return { tipo: 'contenteditable', valor: (host.textContent ?? '') };

    // fallback
    return { tipo: 'input', valor: (host as any).value ?? (host.textContent ?? '') };
  }


  private isSensitiveField(el: HTMLElement): boolean {
    const name = (el.getAttribute('formControlName') || el.getAttribute('name') || el.id || '').toString();
    const type = (el as HTMLInputElement).type || '';
    return type.toLowerCase() === 'password' || ActivityService.SENSITIVE_NAME_RE.test(name);
  }


  private maskValue(el: HTMLElement, raw: any) {
    if (this.CAPTURAR_VALOR_PLANO) return { val: raw, masked: false, strategy: 'none' };
    if (this.isSensitiveField(el)) return { val: raw ? '••••••' : '', masked: true, strategy: 'password' };
    return { val: raw, masked: false, strategy: 'none' };
  }

  private valuesEqual(a: any, b: any): boolean {
    if (Array.isArray(a) || Array.isArray(b)) {
      const A = Array.isArray(a) ? a.slice().sort() : [a];
      const B = Array.isArray(b) ? b.slice().sort() : [b];
      return JSON.stringify(A) === JSON.stringify(B);
    }
    return a === b;
  }

  private fieldKey(el: HTMLElement): string | undefined {
    const host = this.normalizeComboElement(el);
    const direct =
      host.getAttribute('formControlName') ||
      host.getAttribute('formcontrolname') ||
      host.getAttribute('name') ||
      host.id;
    if (direct) return direct;

    const viaClosest =
      host.closest('[formcontrolname]')?.getAttribute('formcontrolname') ||
      host.closest('[formControlName]')?.getAttribute('formControlName') ||
      host.closest('[ng-reflect-form-control-name]')?.getAttribute('ng-reflect-form-control-name');
    return viaClosest || this.findLabelText(host) || undefined;
  }


  private beginEdit(el: HTMLElement) {
    if (this.editSessions.has(el)) return;
    const snap = this.getFieldSnapshot(el);
    this.editSessions.set(el, {
      startTs: Date.now(),
      initial: snap.valor,
      last: snap.valor,
      tipo: snap.tipo,
      campo: this.fieldKey(el),
      changes: 0,
      changed: false,
    });
  }

  private updateEdit(el: HTMLElement) {
    const s = this.editSessions.get(el);
    const snap = this.getFieldSnapshot(el);
    if (!s) { this.beginEdit(el); return this.updateEdit(el); }
    s.last = snap.valor;
    s.changes++;
    // marca si cambió respecto al inicial
    s.changed = s.changed || !this.valuesEqual(s.initial, s.last);
  }

  /** Programa la emisión al SALIR; si no hay blur, emite por timeout. */
  private queueCommit(el: HTMLElement, reason: 'change'|'blur'|'leave') {
    const s = this.editSessions.get(el);
    if (!s) return;
    if (s.timer) clearTimeout(s.timer);
    // si no hay blur (algunos selects), emitimos tras un corto plazo
    s.timer = setTimeout(() => this.finalizeEdit(el, reason, true), 400);
  }

  /** Emite el único log del campo (haya o no cambio) y cierra sesión. */
  private finalizeEdit(el: HTMLElement, reason: 'change'|'blur'|'leave', fromTimer = false) {
    const s = this.editSessions.get(el);
    if (!s) return;

    if (s.timer) { clearTimeout(s.timer); s.timer = undefined; }

    const snap = this.getFieldSnapshot(el);
    const finalVal = snap.valor;
    const huboCambio = s.changed || !this.valuesEqual(s.initial, finalVal);

    const maskedOld = this.maskValue(el, s.initial);
    const maskedNew = this.maskValue(el, finalVal);
    const meta = this.describeElement(el);
    const dur = Date.now() - s.startTs;

    this.emit('Input', meta, this.describeAction('Input', meta), {
      TipoControl: s.tipo,
      Campo: s.campo,
      ValorAnterior: maskedOld.val,
      ValorNuevo: maskedNew.val,
      OpcionTexto: snap.opcionTexto,
      Checked: snap.checked,
      Enmascarado: maskedOld.masked || maskedNew.masked,
      MascaraEstrategia: maskedNew.strategy,
      // NUEVO:
      SinCambio: !huboCambio,
      Commit: reason,
      DatosExtra: { DuracionMs: dur, VecesEditado: s.changes, FromTimer: fromTimer }
    });

    this.editSessions.delete(el);
  }

  // Encuentra el input/host con role="combobox" asociado a la opción pulsada (ng-select / mat-select)
  private findComboboxForOption(optionEl: HTMLElement): HTMLElement | null {
    // ng-select: id típico "<panelId>-<idx>" → aria-controls en el combobox
    const id = optionEl.id;
    if (id && id.includes('-')) {
      const panelId = id.split('-')[0];
      const cb = this.document.querySelector<HTMLElement>(`[role="combobox"][aria-controls="${panelId}"]`);
      if (cb) return cb;
    }

    // CDK overlay: relaciona panel con aria-controls
    const pane = optionEl.closest('.cdk-overlay-pane');
    const panel = pane?.querySelector('[role="listbox"], .ng-dropdown-panel, .mat-select-panel') as HTMLElement | null;
    const panelId = panel?.id;
    if (panelId) {
      const cb = this.document.querySelector<HTMLElement>(`[role="combobox"][aria-controls="${panelId}"]`);
      if (cb) return cb;
    }

    // Fallback: host del control
    const host = optionEl.closest('.ng-select, .mat-select') as HTMLElement | null;
    const cb2 = host?.querySelector<HTMLElement>('[role="combobox"]');
    return cb2 || host || null;
  }

  /** Texto visible actual del combo (ng-select / mat-select / fallback). */
  private readComboboxText(hostOrCombo: HTMLElement): string | string[] {
    const host = this.normalizeComboElement(hostOrCombo);

    // ng-select
    if (host.classList.contains('ng-select')) {
      const labels = Array.from(host.querySelectorAll('.ng-value-label'))
        .map(n => (n.textContent || '').trim())
        .filter(Boolean);
      if (labels.length) {
        const multiple = host.classList.contains('ng-select-multiple');
        return multiple ? labels : labels[0];
      }
      // Si no hay labels (sin selección), devuelve cadena vacía o []
      return host.classList.contains('ng-select-multiple') ? [] : '';
    }

    // mat-select
    if (host.classList.contains('mat-select')) {
      const txt = host.querySelector('.mat-select-value-text') as HTMLElement | null;
      const t = (txt?.textContent || '').trim();
      return t;
    }

    // Fallback genérico
    const val = (host as any).value ?? (host.getAttribute('aria-activedescendant') || '');
    return typeof val === 'string' ? val : String(val ?? '');
  }


  /** Si estás dentro de ng-select/mat-select, devuelve el host del control. */
private normalizeComboElement(el: HTMLElement): HTMLElement {
  const ngHost = el.closest('.ng-select') as HTMLElement | null;
  if (ngHost) return ngHost;

  const matHost = el.closest('.mat-select') as HTMLElement | null;
  if (matHost) return matHost;

  const combo = el.closest('[role="combobox"]') as HTMLElement | null;
  if (combo) {
    const comboNg = combo.closest('.ng-select') as HTMLElement | null;
    if (comboNg) return comboNg;
    const comboMat = combo.closest('.mat-select') as HTMLElement | null;
    if (comboMat) return comboMat;
    return combo;
  }
  return el;
}
private wireRouter() {
  this.router.events.subscribe(ev => {
    if (ev instanceof ActivationEnd && ev.snapshot.outlet === 'primary') {
      this.currentComponentName = this.findComponentName(ev.snapshot);
    }

    if (ev instanceof NavigationEnd) {
      const url = ev.urlAfterRedirects || ev.url;
      this.rutaActual = url;

      // clave de dedupe por navegación
      const routeKey = `${url}|${this.currentComponentName ?? ''}`;
      if (routeKey !== this._lastRouteKey) {
        this._lastRouteKey = routeKey;
        this.emit('Route', undefined, 'Cambio de ruta');
      }
    }
  });

  // Emit inicial (por si entras directo a una ruta)
  const initialKey = `${this.router.url}|${this.currentComponentName ?? ''}`;
    if (initialKey !== this._lastRouteKey) {
      this._lastRouteKey = initialKey;
      this.emit('Route', undefined, 'Carga inicial');
    }
  }

  private wireDom() {
    if (this._domWired) return;   // 👈 evita duplicados
    this._domWired = true;

    this.ngZone.runOutsideAngular(() => {
      const doc = this.document;
      doc.addEventListener('click', e => this.handleDom(e), { passive: true });
      doc.addEventListener('focusin',  e => { /* ... */ }, { passive: true });
      doc.addEventListener('input',    e => { /* ... */ }, { passive: true });
      doc.addEventListener('change',   e => { /* ... */ }, { passive: true });
      doc.addEventListener('focusout', e => { /* ... */ }, { passive: true });
      doc.addEventListener('keydown',  e => this.handleDom(e), { passive: true });

      window.addEventListener('beforeunload', () => {
        // flush + finalize de sesión si aplica
      });
    });
  }

}
